/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	placeholder: {
		id: 'ai-smart-button.ai-smart-button.ai-smart-button-ui.quick-input.placeholder',
		defaultMessage: 'Ask Rovo anything…',
		description: 'Placeholder for the quick question input in the expanded Rovo button',
	},
	clearLabel: {
		id: 'ai-smart-button.ai-smart-button.ai-smart-button-ui.quick-input.clearLabel',
		defaultMessage: 'Clear question',
		description: 'Accessible label for the button that clears the quick question input',
	},
	submitLabel: {
		id: 'ai-smart-button.ai-smart-button.ai-smart-button-ui.quick-input.submitLabel',
		defaultMessage: 'Send question',
		description: 'Accessible label for the button that submits a quick question to Rovo',
	},
});

const styles = cssMap({
	form: {
		display: 'flex',
		alignItems: 'center',
		width: '320px',
		gap: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.200'),
	},
	standaloneSurface: {
		display: 'flex',
		alignItems: 'center',
		height: '48px',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error TS2322 raw box-shadow string is not in the token union
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
	},
	input: {
		minWidth: 0,
		color: token('color.text.inverse'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- styles the native placeholder exposed by Textfield
		'&::placeholder': {
			color: token('color.text.subtlest'),
		},
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		gap: token('space.050'),
	},
	submitButtonWrapper: {
		display: 'flex',
		borderRadius: token('radius.full'),
	},
	disabledSubmitButtonWrapper: {
		backgroundColor: token('color.background.neutral.bold.hovered'),
	},
});

type QuickInputProps = {
	appearance?: 'default' | 'standalone';
	onOverflowChange?: (isOverflowing: boolean) => void;
	onPromptChange?: (prompt: string) => void;
	onStart?: () => void;
	onSubmit?: (prompt: string) => void;
};

export function QuickInput({
	appearance = 'default',
	onOverflowChange,
	onPromptChange,
	onStart,
	onSubmit,
}: QuickInputProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [prompt, setPrompt] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const hasStartedRef = useRef(false);
	const trimmedPrompt = prompt.trim();
	const updateOverflow = useCallback(() => {
		const input = inputRef.current;
		onOverflowChange?.(Boolean(input?.value.trim() && input.scrollWidth > input.clientWidth));
	}, [onOverflowChange]);

	useEffect(() => {
		updateOverflow();
	}, [prompt, updateOverflow]);

	useEffect(() => {
		const input = inputRef.current;
		if (!input || typeof ResizeObserver === 'undefined') {
			return;
		}

		const resizeObserver = new ResizeObserver(updateOverflow);
		resizeObserver.observe(input);

		return () => resizeObserver.disconnect();
	}, [updateOverflow]);

	const form = (
		<form
			css={styles.form}
			data-quick-input
			onSubmit={(event) => {
				event.preventDefault();
				if (!trimmedPrompt) {
					return;
				}
				onSubmit?.(trimmedPrompt);
			}}
		>
			<Textfield
				appearance="none"
				aria-label={formatMessage(i18n.placeholder)}
				placeholder={formatMessage(i18n.placeholder)}
				ref={inputRef}
				testId="rovo-button-quick-input"
				value={prompt}
				onChange={(event) => {
					const nextPrompt = event.currentTarget.value;
					setPrompt(nextPrompt);
					onPromptChange?.(nextPrompt);
					if (!hasStartedRef.current && nextPrompt.trim()) {
						hasStartedRef.current = true;
						onStart?.();
					}
				}}
				onMouseDownCapture={() => {
					const input = inputRef.current;
					if (input && getDocument()?.activeElement !== input) {
						input.setSelectionRange(0, 0);
						input.scrollLeft = 0;
					}
				}}
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- custom text and placeholder colors are required inside the button's dark pill surface
				css={styles.input}
			/>
			<div css={styles.actions}>
				{prompt.length > 0 && (
					<IconButton
						appearance="subtle"
						icon={(iconProps) => (
							<CrossIcon {...iconProps} color={token('color.icon.subtle')} size="small" />
						)}
						label={formatMessage(i18n.clearLabel)}
						onClick={() => {
							setPrompt('');
							onPromptChange?.('');
							inputRef.current?.focus();
						}}
						shape="circle"
						type="button"
					/>
				)}
				<span
					css={[styles.submitButtonWrapper, !trimmedPrompt && styles.disabledSubmitButtonWrapper]}
					data-testid="rovo-button-quick-input-submit-wrapper"
				>
					<IconButton
						appearance="primary"
						icon={(iconProps) => (
							<ArrowRightIcon
								{...iconProps}
								color={!trimmedPrompt ? token('color.icon.subtlest') : undefined}
							/>
						)}
						isDisabled={!trimmedPrompt}
						label={formatMessage(i18n.submitLabel)}
						shape="circle"
						spacing="compact"
						type="submit"
					/>
				</span>
			</div>
		</form>
	);

	if (appearance === 'standalone') {
		return <Box xcss={styles.standaloneSurface}>{form}</Box>;
	}

	return form;
}
